import React, { FC, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import styled from '@emotion/styled';
import {
  TablesAndFloorPlansQuery_venue_floorPlans_tables as TableType,
  TablesAndFloorPlansQuery_venue_floorPlans,
} from '~Scenes/Handy/__queries__';
import { TableComponentProps, TableKey, sortTablesByCode } from '../types';
import { TablesSection, TablesSectionTitle } from '../styles';
import { useButton, useI18n, useLogger } from '~Hooks';
import FloorplanButton from '../FloorplanButton';
import { BaseButton } from '~Scenes/Handy/styles';
import { DragTypes } from '~ComponentsImproved/TSR/Types';
import { DropTargetMonitor, useDrop } from 'react-dnd';
import TableMap from './TableMap';
import SwapIcon from '~/Assets/icons/swap.svg';
import Icon from '~ComponentsImproved/Icon';

const FloorplanButtons = styled.div`
  display: grid;
  grid-gap: 8px;
  grid-template-columns: 1fr 1fr;
  margin: 8px 10px;
  width: 100%;
`;

const ButtonWrapper = styled.div<{ chequeIsDragging?: boolean }>`
  position: fixed;
  bottom: ${({ chequeIsDragging }) => (chequeIsDragging ? '50px' : '12px')};
  left: 12px;
  display: flex;
  align-items: center;
  z-index: 1;
  gap: 8px;
`;

const ShowAllButton = styled(BaseButton)<{ chequeIsDragging: boolean; isOver: boolean }>`
  width: fit-content;
  text-transform: none;
  color: var(--colorBlueGray1300);
  background-color: var(--colorBlueGray200);
  height: 40px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ chequeIsDragging }) =>
    chequeIsDragging &&
    `
    bottom: 50px;
  `}

  ${({ isOver }) =>
    isOver &&
    `
    box-shadow: 0px 0px 5px 5px rgba(255,255,255,0.6);
  `}
`;

const SwitchViewButton = styled(BaseButton)`
  width: fit-content;
  text-transform: none;
  color: #fff;
  background-color: var(--colorBlueGray1200);
  border: 1px solid #fff;
  height: 40px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface Props {
  floorPlans: TablesAndFloorPlansQuery_venue_floorPlans[];
  allTablesSorted: TableType[];
  chequeIsDragging: boolean;
  selectedTableView: 'list' | 'map';
  setSelectedTableView?: (view: 'list' | 'map') => void;
  tableComponent: ({ table, keyType, isMapView }: TableComponentProps) => ReactNode;
}

/*
 * Component responsible for displaying the tables in Handy
 * User can choose between having them in a list view or a map view (map view is the one designed in backoffice)
 */
const TableLayout: FC<Props> = ({
  floorPlans,
  allTablesSorted,
  chequeIsDragging,
  selectedTableView,
  setSelectedTableView,
  tableComponent,
}) => {
  const { i18n } = useI18n();
  const logger = useLogger('handy');

  const [selectedFloorPlan, setSelectedFloorPlan] = useState<{ id: string; name: string }>();
  const timer = useRef<number | undefined>();

  const [{ isOver }, drop] = useDrop({
    accept: [DragTypes.CHEQUE],
    collect: (monitor: DropTargetMonitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  useEffect(() => {
    if (isOver) {
      timer.current = window.setTimeout(() => {
        setSelectedFloorPlan(undefined);
      }, 800);
    } else {
      if (timer.current !== undefined) {
        window.clearTimeout(timer.current);
      }
    }

    return () => {
      if (timer.current !== undefined) {
        window.clearTimeout(timer.current);
      }
    };
  }, [isOver]);

  const showFloorPlanButtons =
    (floorPlans.length > 1 && allTablesSorted.length > 40) || selectedTableView === 'map';

  const tablesToShow = useMemo(() => {
    if (selectedFloorPlan?.id === 'ALL') {
      return allTablesSorted;
    }

    const selectedFloorPlanTables = selectedFloorPlan
      ? floorPlans
          .find((floorPlan) => floorPlan.id === selectedFloorPlan.id)
          ?.tables.filter((table) => !table.deleted)
      : [];

    return sortTablesByCode(selectedFloorPlanTables ?? []);
  }, [selectedFloorPlan, allTablesSorted, floorPlans]);

  const markersToShow = useMemo(() => {
    if (selectedFloorPlan?.id === 'ALL') {
      return [];
    }

    const selectedFloorPlanMarkers = selectedFloorPlan
      ? floorPlans.find((floorPlan) => floorPlan.id === selectedFloorPlan.id)?.markers
      : [];

    return selectedFloorPlanMarkers ?? [];
  }, [selectedFloorPlan, floorPlans]);

  const structuresToShow = useMemo(() => {
    if (selectedFloorPlan?.id === 'ALL') {
      return [];
    }

    const selectedFloorPlanStructures = selectedFloorPlan
      ? floorPlans.find((floorPlan) => floorPlan.id === selectedFloorPlan.id)?.structures
      : [];

    return selectedFloorPlanStructures ?? [];
  }, [selectedFloorPlan, floorPlans]);

  const showAllButton = useButton({
    onClick: () => {
      setSelectedFloorPlan(undefined);
    },
  });

  const switchViewButton = useButton({
    onClick: () => {
      logger.info(`Switching Table Layout to ${selectedTableView === 'list' ? 'map' : 'list'}`);
      if (setSelectedTableView) {
        setSelectedTableView(selectedTableView === 'list' ? 'map' : 'list');
      }
    },
  });

  if (showFloorPlanButtons) {
    return (
      <TablesSection>
        <TablesSectionTitle>
          {selectedFloorPlan?.name ?? i18n('Handy.Floorplans')}
        </TablesSectionTitle>

        {selectedFloorPlan ? (
          <ButtonWrapper chequeIsDragging={chequeIsDragging}>
            <ShowAllButton
              {...showAllButton}
              ref={(node) => drop(node)}
              chequeIsDragging={chequeIsDragging}
              isOver={isOver}>
              {i18n('Handy.Select.Floorplan')}
            </ShowAllButton>
            {!chequeIsDragging && setSelectedTableView && (
              <SwitchViewButton {...switchViewButton}>
                <Icon src={SwapIcon} height="16px" color="var(--colorWhite)" />
                {selectedTableView === 'list'
                  ? i18n('Handy.Tables.SwitchToMap')
                  : i18n('Handy.Tables.SwitchToList')}
              </SwitchViewButton>
            )}
          </ButtonWrapper>
        ) : (
          <>
            {!chequeIsDragging && setSelectedTableView && (
              <ButtonWrapper chequeIsDragging={chequeIsDragging}>
                <SwitchViewButton {...switchViewButton}>
                  <Icon src={SwapIcon} height="16px" color="var(--colorWhite)" />
                  {selectedTableView === 'list'
                    ? i18n('Handy.Tables.SwitchToMap')
                    : i18n('Handy.Tables.SwitchToList')}
                </SwitchViewButton>
              </ButtonWrapper>
            )}
          </>
        )}

        {!selectedFloorPlan && (
          <FloorplanButtons>
            {floorPlans.map((floorplan) => {
              return (
                <FloorplanButton
                  key={floorplan.id}
                  id={floorplan.id}
                  name={floorplan.name}
                  onClick={setSelectedFloorPlan}
                />
              );
            })}
            <FloorplanButton
              id="ALL"
              name={i18n('Handy.Tables.All')}
              onClick={setSelectedFloorPlan}
            />
          </FloorplanButtons>
        )}
        {selectedFloorPlan && tablesToShow && (
          <>
            {selectedTableView === 'map' && selectedFloorPlan.id !== 'ALL' ? (
              <TableMap
                tables={tablesToShow}
                markers={markersToShow}
                structures={structuresToShow}
                tableComponent={tableComponent}
                chequeIsDragging={chequeIsDragging}
              />
            ) : (
              tablesToShow.map((table: TableType) =>
                tableComponent({ table, keyType: TableKey['floor_plan'] }),
              )
            )}
          </>
        )}
      </TablesSection>
    );
  }

  return (
    <>
      {!chequeIsDragging && (
        <ButtonWrapper chequeIsDragging={chequeIsDragging}>
          <SwitchViewButton {...switchViewButton}>
            <Icon src={SwapIcon} height="16px" color="#fff" />
            {i18n('Handy.Tables.SwitchToMap')}
          </SwitchViewButton>
        </ButtonWrapper>
      )}
      {floorPlans.length > 1 &&
        floorPlans.map((floorPlan) => {
          if (!floorPlan.tables.length) {
            return null;
          }
          const sortedTables = sortTablesByCode(floorPlan.tables);
          return (
            <TablesSection key={floorPlan.id}>
              <TablesSectionTitle>{floorPlan.name}</TablesSectionTitle>
              {sortedTables.map((table: TableType) =>
                tableComponent({ table, keyType: TableKey['floor_plan'] }),
              )}
            </TablesSection>
          );
        })}

      <TablesSection>
        {floorPlans.length > 1 && (
          <TablesSectionTitle>{i18n('Handy.Tables.All')}</TablesSectionTitle>
        )}
        {allTablesSorted.map((table: TableType) =>
          tableComponent({ table, keyType: TableKey['all_tables'] }),
        )}
      </TablesSection>
    </>
  );
};

export default TableLayout;
